// @ts-strict-ignore
import type { NodeState, WorkflowNode, WorkflowNodeType } from 'flow/service/graph';
import type { useSnackBar } from '@usealloy/component-library';
import { IconName } from '@usealloy/component-library';
import {
  isActionAnImportedValue, isActionDataObjectEphemeral, isActionOfType, isCustomListAction,
  isUsingActiveListVersion,
} from 'flow/service/actions';
import {
  NODES_SUPPORTING_CONNECTOR,
  NODES_SUPPORTING_MENU,
  NODE_STATES_SUPPORTING_MENU,
} from './constants';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import type { AppDispatch } from '~/store';
import type { DropdownItem } from './NodeDropdownMenu';
import {
  OnDeleteDefaults,
  OnSwapDefaults,
  OnUpdateDefaults,
  OnViewListDefaults,
} from './NodeDropdownMenu';
import type { Action, PublicId } from '~/dashboard/views/workflows/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { getIsMergeImpacted } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';
import { createWorkflowHistoryThunk, duplicateNodeThunk } from 'flow/store/redux/thunks';
import upgradeLinkedSnippetVersion from 'flow/api/localSnippets/upgradeLinkedSnippetVersion';
import type { API } from '~/helpers/api-new/modules';
import SnippetVersionBadge from './NodeDropdownMenu/SnippetVersionBadge';
import { ViewIntegrationMenuIcon } from './NodeDropdownMenu/ViewIntegrationMenuIcon';
import { libraryRoutes } from '~/helpers/api-new/modules/library/libraryRoutes';
import type { SnippetOperation, WorkflowCopyPasteSupport } from 'flow/store/redux/types';
import { changeDescription } from 'flow/store/redux/types';
import type { Operation as MergeDiffOperation } from 'flow/service/changelog/types';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { generatePublicId } from 'flow/service/publicIds';
import type { FeatureFlags, Permissions } from '~/helpers/typescript';
import type { PublishedAttributeObjectType } from '~/dashboard/views/settings/published-attributes/types';
import { routes } from '~/helpers/routes';

export function doesNodeTypeSupportConnector(
  nodeType: WorkflowNodeType,
): boolean {
  return NODES_SUPPORTING_CONNECTOR.includes(nodeType);
}

export function doesNodeTypeSupportMenu(nodeType: WorkflowNodeType): boolean {
  return NODES_SUPPORTING_MENU.includes(nodeType);
}

export function doesNodeStateSupportMenu(nodeState: NodeState): boolean {
  return NODE_STATES_SUPPORTING_MENU.includes(nodeState);
}

export function showNodeMenu(nodeState: NodeState, nodeType: WorkflowNodeType): boolean {
  if (!doesNodeTypeSupportMenu(nodeType)) {
    return false;
  }

  return doesNodeStateSupportMenu(nodeState);
}

export function canDuplicateNode(nodeType: WorkflowNodeType): boolean {
  return nodeType !== 'snippet' && nodeType !== 'riskCalculation';
}


export function doesActionSupportTooltip(action: Action): boolean {
  if (!action) {
    return false;
  }

  return !isActionAnImportedValue(action);
}

export function getAttributeNodeIcon(args: {
  defaultIcon: IconName;
  isPublishedAttribute: boolean;
  objectType?: PublishedAttributeObjectType;
  customTransactionAttributesEnabled?: boolean;
}): IconName {
  const {
    defaultIcon,
    isPublishedAttribute,
    objectType,
    customTransactionAttributesEnabled,
  } = args;

  // Default behavior for getAttributeNodeIcon when feature flag is not set
  if (!customTransactionAttributesEnabled) {
    const iconName = isPublishedAttribute ? IconName.Bookmark : defaultIcon;
    return iconName;
  }

  // Feature flag behavior: Determine icon based on published attribute type
  if (isPublishedAttribute) {
    // Only differentiate by object type if feature flag is enabled
    if (objectType === 'transaction') {
      return IconName.MoneyBracket; // Transaction icon
    }
    if (objectType === 'bank_account') {
      return IconName.Bank; // Bank account icon
    }
    // TODO: pick a dedicated session icon once one is available in the component library
    if (objectType === 'session') {
      return IconName.Person; // Session icon
    }
    return IconName.Person; // Entity icon (default for published attributes)
  }

  return defaultIcon; // Default for non-published attributes
}

export function isAddedOrModifiedBySnippetUpdate(args: {
  snippetOperation: SnippetOperation;
  mergeDiffOperation: MergeDiffOperation;
}): boolean {
  const { snippetOperation, mergeDiffOperation } = args;

  if (snippetOperation !== 'upgradeLinkedSnippet') return false;
  if (mergeDiffOperation === 'add' || mergeDiffOperation === 'modify') {
    return true;
  }
  return false;
}

export function getDropdownItems({
  addSnackBar,
  canCreateSnippet,
  canDuplicate,
  closeRuleTooltip,
  dispatch,
  libraryApi,
  nodeId,
  nodeType,
  onCopy,
  onUpdateAction,
  permissions,
  setNodeDeletionChildActionCounts,
  typhonDomain,
  workflowData,
  workflowProps,
  featureFlags,
  wfSupportsCopyPaste,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  canCreateSnippet: boolean;
  canDuplicate: boolean;
  closeRuleTooltip: () => void;
  dispatch: AppDispatch;
  libraryApi: API['library'];
  nodeId: WorkflowNode['id'];
  nodeType: WorkflowNodeType;
  onCopy: (arg?: WorkflowNode['id']) => void;
  onUpdateAction: React.Dispatch<React.SetStateAction<boolean>>;
  permissions: Permissions;
  setNodeDeletionChildActionCounts: React.Dispatch<React.SetStateAction<number>>;
  typhonDomain: string;
  workflowData: WorkflowState['workflowData'];
  workflowProps: WorkflowState['workflowProps'];
  featureFlags: Partial<FeatureFlags>;
  wfSupportsCopyPaste: WorkflowCopyPasteSupport;
}): DropdownItem[] {
  const action = workflowData?.actions?.[nodeId];
  const customLists = workflowProps?.customLists;

  const isCustomListService = isCustomListAction(action);
  const isAlreadyActive = isCustomListService && isUsingActiveListVersion(action, customLists);
  // Ephemeral lists (e.g. from a pasted snippet) aren't saved yet, so there's nothing to view.
  const isEphemeralCustomList = isCustomListService && isActionDataObjectEphemeral(action);

  const isPartnerCenterV2Enabled = featureFlags?.enablePartnerCenterV2;
  const canViewIntegration = Boolean(
    isPartnerCenterV2Enabled
    && permissions['READ:ServiceProducts:null']
    && permissions['READ:Services:null'],
  );
  const serviceInternalName = action?.service?.internalName;
  const showViewIntegrationOption = Boolean(
    isPartnerCenterV2Enabled
    && nodeType === ActionType.SERVICE
    && serviceInternalName,
  );

  let nextLinkedSnippetVersion = null;

  // include existence check for action.snippet
  // sometimes the action data is removed before the node component is unmounted in time
  const isSnippet = isActionOfType(action, ActionType.SNIPPET) && Boolean(action.snippet);
  if (isSnippet) {
    const snippetToken = action.snippet?.token;
    // it's safe to read it from workflowProps
    // as we can only ever upgrade to the latest published version,
    nextLinkedSnippetVersion = workflowProps?.snippets[snippetToken]?.published_version;
  }

  const dropdownItems: DropdownItem[] = [
    ...(featureFlags.copyPasteGA ? [{
      label: 'Copy',
      onSelect: () => onCopy(nodeId),
      Icon: null,
      style: null,
      disabled: !wfSupportsCopyPaste.supported,
      tooltipContent: wfSupportsCopyPaste.reason,
    }] : []),
    ...(canDuplicate ? [{
      label: 'Duplicate',
      onSelect: () => {
        const duplicatedActionPublicId = generatePublicId('action', action.type);

        dispatch(createWorkflowHistoryThunk(
          () => dispatch(duplicateNodeThunk(nodeId, duplicatedActionPublicId)),
          Boolean(featureFlags.workflowEditorUndoRedo),
          {
            operation: Operation.Add,
            uiEffectContext: createUiEffectContext(changeDescription.action, {
              publicId: duplicatedActionPublicId,
            }),
          },
        ));
        closeRuleTooltip();
      },
      Icon: null,
      style: null,
      dataId: 'alloydd-workflow-duplicate-node',
    }] : []),
    ...(nodeType === ActionType.TAG ? [{
      label: OnSwapDefaults.label,
      onSelect: OnSwapDefaults.onSelect({
        dispatch,
        nodeId,
      }),
      Icon: null,
      style: null,
    }] : []),
    ...(isCustomListService ? [{
      label: OnViewListDefaults.label,
      onSelect: OnViewListDefaults.onSelect({
        nodeId,
        workflowData,
      }),
      Icon: OnViewListDefaults.Icon,
      style: OnViewListDefaults.style,
      disabled: isEphemeralCustomList,
      tooltipContent: isEphemeralCustomList
        ? 'This list will be created when you save the workflow.'
        : undefined,
    }, {
      label: OnUpdateDefaults.label,
      onSelect: OnUpdateDefaults.onSelect({
        onUpdateAction,
      }),
      Icon: null,
      style: null,
      disabled: isAlreadyActive,
    }] : []),
    ...(isSnippet ? [
      {
        disabled: !canCreateSnippet,
        label: 'Edit Snippet',
        onSelect: () => {
          const url = libraryRoutes.localSnippetVersionUpdate(
            action.snippet.token,
            action.snippet.publicVersion,
          );
          window.open(url, '_blank');
        },
        Icon: null,
        style: null,
      },
      // TODO: Implement detach from library
      // {
      //   label: 'Detach from Library',
      //   onSelect: () => {},
      //   Icon: null,
      //   style: null,
      // },
      {
        disabled: nextLinkedSnippetVersion == null
          || action.snippet.publicVersion === nextLinkedSnippetVersion,
        label: 'Update to latest published',
        onSelect: () => {
          if (nextLinkedSnippetVersion != null) {
            upgradeLinkedSnippetVersion({
              addSnackBar,
              currentVersion: action.snippet.publicVersion,
              dispatch,
              libraryApi,
              localSnippetActionPublicId: nodeId,
              nextVersion: nextLinkedSnippetVersion,
              snippetToken: action.snippet.token,
              typhonDomain,
              workflowActions: workflowData.actions,
              workflowProps,
            });
          }
        },
        // we call the component's function instead of using JSX as this file is TS only
        Icon: SnippetVersionBadge({ versionNumber: nextLinkedSnippetVersion }),
        style: {
          display: 'flex',
          flexDirection: 'row-reverse',
        },
      } as DropdownItem,
    ] : []),
    {
      label: OnDeleteDefaults.label,
      onSelect: OnDeleteDefaults.onSelect({
        addSnackBar,
        dispatch,
        isWorkflowHistoryOn: Boolean(featureFlags?.workflowEditorUndoRedo),
        nodeId,
        workflowData,
        setNodeDeletionChildActionCounts,
      }),
      Icon: OnDeleteDefaults.Icon,
      style: OnDeleteDefaults.style,
    },
    ...(showViewIntegrationOption ? [{
      label: 'View Integration',
      onSelect: () => {
        window.open(
          routes.v3.dashboard.partnerSolutions.detail(serviceInternalName),
          '_blank',
          'noopener,noreferrer',
        );
      },
      Icon: ViewIntegrationMenuIcon,
      style: null,
      disabled: !canViewIntegration,
      tooltipContent: !canViewIntegration
        ? 'You don\'t have access to Partner Integrations. Request access to manage partner settings.'
        : undefined,
      dataId: 'alloydd-workflow-view-integration',
      isNewGroup: true,
    }] : []),
  ];

  return dropdownItems;
}

/**
  * Disallow hover treatment if we are merging and this is not a merge
  * impacted node. Allow otherwise.
  */
export function getCanHoverOnNode(mergeDiff: MergeDiffResult | null, nodeId: PublicId) {
  return !mergeDiff || (mergeDiff && getIsMergeImpacted(mergeDiff, nodeId));
}
